const API_BASE = import.meta.env.VITE_API_BASE || '';

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const authStore = await import('../hooks/useAuthStore');
  const token = authStore.useAuthStore.getState().adminToken;
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(url, { ...options, headers });
  
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${res.status}`);
  }
  
  return res.json();
}

export const api = {
  upload: async (ffId: string, screenshot: File) => {
    const formData = new FormData();
    formData.append('ffId', ffId);
    formData.append('screenshot', screenshot);
    
    const res = await fetch(`${API_BASE}/api/upload`, {
      method: 'POST',
      body: formData,
    });
    
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Upload failed');
    }
    
    return res.json() as Promise<{ success: boolean; submissionId?: string; error?: string }>;
  },

  getSubmissions: async (ffId: string) => {
    return request<{ submissions: any[] }>(`/api/submissions/user/${ffId}`);
  },

  getSubmission: async (id: string) => {
    return request<{ submission: any }>(`/api/submissions/${id}`);
  },

  // Admin
  getPendingSubmissions: async () => {
    return request<{ submissions: any[] }>('/api/admin/submissions');
  },

  adminAction: async (submissionId: string, action: 'approve' | 'reject', rejectionReason?: string) => {
    return request<{ success: boolean; submission: any }>('/api/admin/action', {
      method: 'POST',
      body: JSON.stringify({ submissionId, action, rejectionReason }),
    });
  },

  getStats: async () => {
    return request<{ total: number; pending: number; approved: number; rejected: number }>('/api/admin/stats');
  },
};

export function createWebSocketUrl(userId?: string, isAdmin = false): string {
  const wsBase = import.meta.env.VITE_WS_BASE || (location.protocol === 'https:' ? 'wss:' : 'ws:') + '//' + location.host;
  const params = new URLSearchParams();
  if (userId) params.set('userId', userId);
  if (isAdmin) params.set('isAdmin', 'true');
  return `${wsBase}/api/ws?${params.toString()}`;
}